import Dexie, { type EntityTable } from 'dexie';
import { createInitialGame, CURRENT_SAVE_VERSION, DEFAULT_COMPANY_NAME } from '../game/createGame';
import { completeTutorialIfReady } from '../game/tutorial';
import { createResearchOpportunity } from '../game/quizFlow';
import { addAdvancedAiManager, addAiManagers, addEliteAiManager, addStrategicAiManager } from '../game/aiManagers';
import { updateAchievements } from '../game/achievements';
import { DEFAULT_NEW_GAME_OPTIONS, type NewGameOptions } from '../game/scenarios';
import { applyChallengeStart } from '../game/challengeStarts';
import type { CabinClass, FuelStorageFacility, GameState, RouteStats } from '../game/types';

class NoFlightDatabase extends Dexie {
  games!: EntityTable<GameState, 'id'>;

  constructor() {
    // Keep the original database key so existing local saves survive the rename.
    super('petit-flight');
    this.version(1).stores({ games: 'id, updatedAt, week' });
  }
}

export const db = new NoFlightDatabase();

function emptyCabins(): Record<CabinClass, { seats: number; demand: number; passengers: number; revenue: number; loadFactor: number; unmetDemand: number }> {
  return {
    economy: { seats: 0, demand: 0, passengers: 0, revenue: 0, loadFactor: 0, unmetDemand: 0 },
    business: { seats: 0, demand: 0, passengers: 0, revenue: 0, loadFactor: 0, unmetDemand: 0 },
    first: { seats: 0, demand: 0, passengers: 0, revenue: 0, loadFactor: 0, unmetDemand: 0 },
  };
}

function migrateRouteStats(stats: RouteStats): RouteStats {
  return {
    ...stats,
    cabins: stats.cabins ?? emptyCabins(),
  };
}

function sanitizeNumber(value: unknown, fallback = 0): number {
  return typeof value === 'number' && Number.isFinite(value) ? value : fallback;
}

function sanitizeRouteStats(stats: RouteStats): RouteStats {
  const sanitized: RouteStats = {
    distanceKm: sanitizeNumber(stats.distanceKm),
    roundTrips: sanitizeNumber(stats.roundTrips),
    seats: sanitizeNumber(stats.seats),
    demand: sanitizeNumber(stats.demand),
    passengers: sanitizeNumber(stats.passengers),
    localPassengers: sanitizeNumber(stats.localPassengers),
    transferPassengers: sanitizeNumber(stats.transferPassengers),
    loadFactor: sanitizeNumber(stats.loadFactor),
    unmetDemand: sanitizeNumber(stats.unmetDemand),
    revenue: sanitizeNumber(stats.revenue),
    cashCost: sanitizeNumber(stats.cashCost),
    operatingProfit: sanitizeNumber(stats.operatingProfit),
    networkContribution: sanitizeNumber(stats.networkContribution),
    cabins: { economy: emptyCabins().economy, business: emptyCabins().business, first: emptyCabins().first },
  };
  for (const cabin of (['economy', 'business', 'first'] as const)) {
    const source = stats.cabins?.[cabin] ?? emptyCabins()[cabin];
    sanitized.cabins[cabin] = {
      seats: sanitizeNumber(source.seats),
      demand: sanitizeNumber(source.demand),
      passengers: sanitizeNumber(source.passengers),
      revenue: sanitizeNumber(source.revenue),
      loadFactor: sanitizeNumber(source.loadFactor),
      unmetDemand: sanitizeNumber(source.unmetDemand),
    };
  }
  return sanitized;
}

const FUEL_STORAGE_LIFE_WEEKS = 1040;
const FUEL_STORAGE_CAPACITY = 500_000;
const FUEL_STORAGE_PRICE = 1_000_000;

function migrateFuelStorage(state: GameState): FuelStorageFacility[] {
  const storage = state.fuelStorage as GameState['fuelStorage'] | { level?: number; capacity?: number; builtWeek?: number; weeklyCost?: number } | undefined;
  if (Array.isArray(storage)) return storage;
  const old = storage as { level?: number; capacity?: number; builtWeek?: number; weeklyCost?: number } | undefined;
  if (!old || typeof old.level !== 'number') return [];
  const count = old.level * 2;
  const builtWeek = old.builtWeek ?? 0;
  const elapsed = Math.max(0, Math.min(FUEL_STORAGE_LIFE_WEEKS, state.week - builtWeek));
  const remaining = Math.max(0, FUEL_STORAGE_LIFE_WEEKS - elapsed);
  const bookValueRatio = remaining / FUEL_STORAGE_LIFE_WEEKS;
  const facilities: FuelStorageFacility[] = [];
  for (let i = 0; i < count; i += 1) {
    facilities.push({
      id: `fuel-storage-${state.nextId++}`,
      capacity: FUEL_STORAGE_CAPACITY,
      builtWeek,
      purchasePrice: FUEL_STORAGE_PRICE,
      bookValue: Math.round(FUEL_STORAGE_PRICE * bookValueRatio),
      remainingLifeWeeks: remaining,
      totalLifeWeeks: FUEL_STORAGE_LIFE_WEEKS,
    });
  }
  return facilities;
}

export function migrateGame(raw: GameState): GameState {
  const migrated = structuredClone(raw);
  migrated.saveVersion = CURRENT_SAVE_VERSION;
  migrated.reports ??= [];
  migrated.ledger ??= [];
  migrated.loans ??= [];
  migrated.loans = migrated.loans.map((loan) => {
    const kind = loan.kind ?? 'aircraft';
    return {
      ...loan,
      kind,
      weeklyPayment: kind === 'bank' ? loan.principalRemaining * loan.annualRate / 12 / 4 : loan.weeklyPayment,
    };
  });
  migrated.events ??= [];
  migrated.nextId ??= 10_000;
  migrated.quizCounter ??= 0;
  migrated.pendingQuiz ??= false;
  migrated.lastQuizWeek ??= 0;
  migrated.quizModeEnabled ??= migrated.singlePlayerDifficulty !== 'beginner';
  migrated.researchHistory ??= [];
  if (migrated.pendingQuiz && !migrated.researchOpportunity) {
    migrated.researchOpportunity = createResearchOpportunity(migrated);
    migrated.pendingQuiz = false;
  }
  migrated.activeEffects ??= [];
  migrated.activeMarketEvents ??= [];
  migrated.marketEventResolutions ??= [];
  migrated.vouchers ??= {};
  migrated.assistants = {
    unlockedIds: migrated.assistants?.unlockedIds ?? [],
    dismissedIds: migrated.assistants?.dismissedIds ?? [],
    hired: migrated.assistants?.hired ?? [],
  };
  migrated.routes = (migrated.routes ?? []).map((route) => {
    const { strategy: _strategy, aircraftStrategies: _aircraftStrategies, ...rest } = route as typeof route & { strategy?: unknown; aircraftStrategies?: unknown };
    const legacyFare = (route as typeof route & { fareStrategy?: 'low' | 'standard' | 'premium' }).fareStrategy;
    const fareRatioMap: Record<'low' | 'standard' | 'premium', number> = { low: 85, standard: 100, premium: 120 };
    return {
      ...rest,
      active: rest.active ?? true,
      reputationScore: rest.reputationScore ?? 25,
      fareRatio: rest.fareRatio ?? (legacyFare ? fareRatioMap[legacyFare] : 100),
      stats: migrateRouteStats(rest.stats),
    };
  });
  const routeFareByAircraftId = new Map<string, number>();
  for (const route of migrated.routes ?? []) {
    for (const aircraftId of route.aircraftIds ?? []) {
      routeFareByAircraftId.set(aircraftId, route.fareRatio ?? 100);
    }
  }
  migrated.aircraft = (migrated.aircraft ?? []).map((aircraft) => {
    const legacyRouteId = (aircraft as typeof aircraft & { routeId?: string }).routeId;
    const routeIds = (aircraft.routeIds ?? (legacyRouteId ? [legacyRouteId] : []));
    const { routeId: _routeId, ...rest } = aircraft as typeof aircraft & { routeId?: string };
    return {
      ...rest,
      routeIds,
      totalCycles: aircraft.totalCycles ?? 0,
      totalFlightHours: aircraft.totalFlightHours ?? 0,
      speedModifier: aircraft.speedModifier ?? 1,
      fareRatio: aircraft.fareRatio ?? routeFareByAircraftId.get(aircraft.id) ?? 100,
      crewSalaryModifier: aircraft.crewSalaryModifier ?? 1,
      crewMood: aircraft.crewMood ?? 60,
      crewHiredWeek: aircraft.crewHiredWeek ?? aircraft.acquiredWeek,
    };
  });
  migrated.fuelInventory ??= { gallons: 0, averageCost: 0 };
  migrated.fuelFutures ??= [];
  migrated.fuelStorage = migrateFuelStorage(migrated);
  migrated.fuelStorageQueue ??= 0;
  migrated.fuelStorageBuiltThisWeek ??= 0;
  migrated.companyListing ??= { listed: false, listedWeek: 0, totalShares: 0, outstandingShares: 0, treasuryShares: 0, sharePrice: 0, lastValuation: 0, sharePriceHistory: [] };
  migrated.companyListing.sharePriceHistory ??= [];
  migrated.companyListing.institutionalShares ??= 0;
  migrated.playerAssets = {
    cash: migrated.playerAssets?.cash ?? 50_000_000,
    shares: migrated.playerAssets?.shares ?? 0,
    stockPortfolio: migrated.playerAssets?.stockPortfolio ?? {},
    stockPortfolioCost: migrated.playerAssets?.stockPortfolioCost ?? {},
  };
  migrated.dividendRecords ??= [];
  migrated.ventureRounds ??= [];
  migrated.aiManagers = (migrated.aiManagers ?? []).map((manager) => {
    const lastDecisionCash = manager.decisions?.length
      ? manager.decisions[manager.decisions.length - 1].cashAfter
      : undefined;
    const recoveredCash = sanitizeNumber(manager.cash, typeof lastDecisionCash === 'number' && Number.isFinite(lastDecisionCash) ? lastDecisionCash : 0);
    return {
      ...manager,
      cash: recoveredCash,
      strategy: manager.strategy ?? 'basic',
      hubs: manager.hubs ?? [],
      aircraft: (manager.aircraft ?? []).map((aircraft) => ({
        ...aircraft,
        routeIds: aircraft.routeIds ?? [],
        totalCycles: aircraft.totalCycles ?? 0,
        totalFlightHours: aircraft.totalFlightHours ?? 0,
        speedModifier: aircraft.speedModifier ?? 1,
        fareRatio: aircraft.fareRatio ?? 100,
        crewSalaryModifier: aircraft.crewSalaryModifier ?? 1,
        crewMood: aircraft.crewMood ?? 60,
        crewHiredWeek: aircraft.crewHiredWeek ?? aircraft.acquiredWeek,
      })),
      routes: (manager.routes ?? []).map((route) => {
        const legacyFare = (route as typeof route & { fareStrategy?: 'low' | 'standard' | 'premium' }).fareStrategy;
        const fareRatioMap: Record<'low' | 'standard' | 'premium', number> = { low: 85, standard: 100, premium: 120 };
        return {
          ...route,
          active: route.active ?? true,
          fareRatio: (route as typeof route & { fareRatio?: number }).fareRatio ?? (legacyFare ? fareRatioMap[legacyFare] : 100),
          stats: sanitizeRouteStats(route.stats),
        };
      }),
      loans: manager.loans ?? [],
      decisions: manager.decisions ?? [],
      routeMemory: manager.routeMemory ?? {},
      companyListing: manager.companyListing
        ? {
          ...manager.companyListing,
          founderShares: manager.companyListing.founderShares ?? (manager.companyListing.totalShares > 0 ? Math.round(manager.companyListing.totalShares * 0.8) : undefined),
          institutionalShares: manager.companyListing.institutionalShares ?? 0,
          sharePriceHistory: manager.companyListing.sharePriceHistory ?? [],
        }
        : undefined,
    };
  });
  migrated.milestones ??= { achievedIds: [], lastCheckedWeek: migrated.week ?? 1 };
  migrated.victories ??= [];
  migrated.achievements ??= [];
  migrated.careerRecords ??= { peakFleetSize: migrated.aircraft?.length ?? 0, peakActiveRoutes: migrated.routes?.filter((route) => route.active).length ?? 0, peakHubCount: migrated.hubs?.length ?? 0, totalPassengers: migrated.reports?.reduce((sum, report) => sum + (report.uniquePassengers ?? 0), 0) ?? 0, acquiredAirlines: 0 };
  migrated.shareholderMandate ??= undefined;
  migrated.gameOver ??= undefined;
  migrated.tutorialCompleted ??= false;
  migrated.companyName = typeof migrated.companyName === 'string' && migrated.companyName.trim() ? migrated.companyName : DEFAULT_COMPANY_NAME;
  migrated.scenarioId ??= 'sandbox';
  const legacyDifficulty = migrated.singlePlayerDifficulty as string | undefined;
  migrated.singlePlayerDifficulty = legacyDifficulty === 'relaxed'
    ? 'beginner'
    : legacyDifficulty === 'standard'
      ? 'experienced'
      : legacyDifficulty === 'challenging'
        ? 'challenge'
        : legacyDifficulty === 'beginner' || legacyDifficulty === 'experienced' || legacyDifficulty === 'expert' || legacyDifficulty === 'challenge'
          ? legacyDifficulty
          : 'beginner';
  return updateAchievements(completeTutorialIfReady(migrated));
}

export const MAX_SAVES = 5;

const ACTIVE_SAVE_KEY = 'noflight-active-save';
const LEGACY_SAVE_ID = 'main';

export interface SaveSummary {
  id: string;
  companyName: string;
  week: number;
  cash: number;
  createdAt: number;
  updatedAt: number;
  gameOver: boolean;
}

export function getActiveSaveId(): string {
  if (typeof window === 'undefined') return LEGACY_SAVE_ID;
  return window.localStorage.getItem(ACTIVE_SAVE_KEY) ?? LEGACY_SAVE_ID;
}

function setActiveSaveId(id: string): void {
  if (typeof window === 'undefined') return;
  window.localStorage.setItem(ACTIVE_SAVE_KEY, id);
}

export async function listSaves(): Promise<SaveSummary[]> {
  const all = await db.games.toArray();
  return all
    .map((game) => ({
      id: game.id,
      companyName: typeof game.companyName === 'string' && game.companyName.trim() ? game.companyName : DEFAULT_COMPANY_NAME,
      week: game.week ?? 1,
      cash: sanitizeNumber(game.cash),
      createdAt: game.createdAt ?? 0,
      updatedAt: game.updatedAt ?? 0,
      gameOver: !!game.gameOver,
    }))
    .sort((a, b) => a.createdAt - b.createdAt);
}

export async function loadGame(): Promise<GameState> {
  const stored = (await db.games.get(getActiveSaveId())) ?? (await db.games.get(LEGACY_SAVE_ID));
  if (stored) {
    setActiveSaveId(stored.id);
    return migrateGame(stored);
  }
  const initial = createInitialGame();
  await db.games.put(initial);
  setActiveSaveId(initial.id);
  return initial;
}

export async function loadSave(id: string): Promise<GameState> {
  const stored = await db.games.get(id);
  if (!stored) throw new Error('没有找到这个存档');
  setActiveSaveId(stored.id);
  return migrateGame(stored);
}

function withConfiguredAi(initial: GameState, options: NewGameOptions): GameState {
  let next = initial;
  const count = Math.min(5, Math.max(0, Math.floor(options.aiCount)));
  if (count === 0) return next;
  if (options.difficulty === 'beginner') return addAiManagers(next, count);
  if (options.difficulty === 'challenge') {
    for (let i = 0; i < count; i += 1) next = i % 2 === 0 ? addStrategicAiManager(next) : addEliteAiManager(next);
    return next;
  }
  for (let i = 0; i < count; i += 1) {
    if (i === 0) next = addAiManagers(next, 1);
    else if (i % 2 === 1) next = addAdvancedAiManager(next);
    else next = addStrategicAiManager(next);
  }
  return next;
}

export async function createSave(options: NewGameOptions = DEFAULT_NEW_GAME_OPTIONS): Promise<GameState> {
  const count = await db.games.count();
  if (count >= MAX_SAVES) throw new Error(`最多只能同时保留 ${MAX_SAVES} 个存档`);
  const seed = options.seed ?? Math.floor(Math.random() * 2_000_000_000);
  let initial = withConfiguredAi(createInitialGame(seed, options), options);
  if (options.difficulty === 'challenge' && options.challengeStartId) initial = applyChallengeStart(initial, options.challengeStartId);
  initial.id = `save-${Date.now().toString(36)}-${Math.floor(Math.random() * 1_000_000).toString(36)}`;
  await db.games.put(initial);
  setActiveSaveId(initial.id);
  return initial;
}

export async function deleteSave(id: string): Promise<void> {
  if (id === getActiveSaveId()) throw new Error('不能删除正在游玩的存档');
  await db.games.delete(id);
}

export async function saveGame(state: GameState): Promise<void> {
  await db.games.put(state);
}

export async function resetStoredGame(options: NewGameOptions = DEFAULT_NEW_GAME_OPTIONS): Promise<GameState> {
  const activeId = getActiveSaveId();
  const current = await db.games.get(activeId);
  const seed = options.seed ?? Math.floor(Math.random() * 2_000_000_000);
  // Reset is not a new-save flow, so it keeps the current save's birthplace without exposing a selector.
  const resetOptions = current?.headquartersId && options.headquartersId === undefined
    ? { ...options, headquartersId: current.headquartersId }
    : options;
  const initial = withConfiguredAi(createInitialGame(seed, resetOptions), resetOptions);
  initial.id = activeId;
  if (current?.companyName) initial.companyName = current.companyName;
  await db.games.put(initial);
  return initial;
}
